'use client';

/**
 * VendorPortalRfqListCard — seller's RFQ inbox (explorer surface).
 *
 * Mode5-native parallel paint. Doesn't import the legacy
 * `<VendorPortalExplorer />`; doctrinal parity with mode4's
 * `VendorListCard` pattern (duplicate, not reuse, so mode5 can
 * evolve independently of the legacy mode during cohabitation).
 *
 * Renders the RFQ inbox for the active seller — each row shows the
 * reference, project, line count, deadline, and state. Click
 * advances the viewport selection to the chosen RFQ; the matrix
 * routes to `mode5_quote_form` and the canvas paints the quote form.
 *
 * @see klyde_edge/ui_config/composition/cards/seller.py
 *      content_key="vendor_portal_rfq_list"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useEffect, useState } from 'react';
import {
  ClipboardPen,
  FileText,
  Clock,
  AlertCircle,
  ShieldAlert,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useSellerLabel } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { text, treeDepth, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { VPRfqSummary, VPRfqListResponse } from '@/lib/api/types';

export function VendorPortalRfqListCard() {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const listStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'list'));
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  // Server-driven labels.
  const labelTitle = useSellerLabel('explorer_rfq_title');
  const labelLoading = useSellerLabel('explorer_loading');
  const labelNoRole = useSellerLabel('explorer_no_role');
  const labelNoUser = useSellerLabel('explorer_no_user');
  const labelNoRfqs = useSellerLabel('explorer_no_rfqs');
  const labelNoProject = useSellerLabel('explorer_rfq_no_project');

  const [rfqs, setRfqs] = useState<VPRfqSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedUser || !listStrategy) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    api
      .getStrategyData<VPRfqListResponse>(listStrategy, {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load RFQs');
          return;
        }
        setRfqs(res.data?.rfqs ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUser, listStrategy]);

  if (!listStrategy) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'p-4 text-center')}>
        <ShieldAlert className="w-4 h-4 mx-auto mb-2 opacity-50" />
        {labelNoRole}
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'p-4 text-center')}>
        <AlertCircle className="w-4 h-4 mx-auto mb-2 opacity-50" />
        {labelNoUser}
      </div>
    );
  }

  if (isLoading && rfqs.length === 0) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'flex items-center gap-2 px-3 py-2')}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {labelLoading}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'px-3 py-2 text-destructive flex items-center gap-2')}>
        <XCircle className="h-3.5 w-3.5" />
        {error}
      </div>
    );
  }

  if (rfqs.length === 0) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'p-4 text-center')}>
        {labelNoRfqs}
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="space-y-1">
      <div className="flex items-center justify-between gap-2 px-2">
        <div className={cn(text.heading, 'flex items-center gap-1.5')}>
          <ClipboardPen className="h-3.5 w-3.5" />
          {labelTitle}
        </div>
        <span className={cn(text.muted, 'opacity-70')}>{rfqs.length}</span>
      </div>

      <div className="py-1">
        {rfqs.map((rfq) => {
          const isSelected = selectedNode?.id === rfq.rfq_sid;
          return (
            <button
              key={rfq.rfq_sid}
              onClick={() => {
                // Two-step selection so the canvas + rail BOTH update.
                // Canvas keys off `strategySelection` (resolved as
                // `explorerSelection ?? tabType` per
                // `lib/stores/viewport-selection.ts`); rail keys off
                // `selectedNode`. Setting only one leaves the canvas
                // stuck on the default preset (mode5_landing) instead
                // of switching to mode5_quote_form.
                selectNode({
                  id: rfq.rfq_sid,
                  label: rfq.reference ?? rfq.rfq_sid,
                  type: 'rfq',
                });
                setExplorerSelection('rfq');
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left',
                'hover:bg-accent/50 transition-colors border-b border-border/30',
                isSelected && 'bg-accent',
              )}
            >
              <FileText className="w-4 h-4 shrink-0 text-yellow-400" />
              <div className="flex-1 min-w-0">
                <div className={cn(treeDepth[0], 'truncate')}>{rfq.reference ?? '—'}</div>
                <RfqRowSubtitle
                  projectName={rfq.project_name}
                  lineCount={rfq.line_count}
                  noProjectLabel={labelNoProject}
                />
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {rfq.deadline && (
                  <span className={cn(text.muted, 'flex items-center gap-1')}>
                    <Clock className="w-3 h-3" />
                    {new Date(rfq.deadline).toLocaleDateString()}
                  </span>
                )}
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded',
                    rfq.state === 'sent' && 'bg-blue-500/20 text-blue-500',
                    rfq.state === 'draft' && 'bg-gray-500/20 text-gray-500',
                    rfq.state === 'done' && 'bg-green-500/20 text-green-500',
                  )}
                >
                  {rfq.state ?? '—'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RfqRowSubtitle({
  projectName,
  lineCount,
  noProjectLabel,
}: {
  projectName: string | null;
  lineCount: number;
  noProjectLabel: string;
}) {
  const itemsLabel = useSellerLabel('explorer_rfq_items_suffix', { count: lineCount });
  return (
    <div className={cn(text.muted, 'truncate')}>
      {projectName ?? noProjectLabel}
      {lineCount > 0 && ` · ${itemsLabel}`}
    </div>
  );
}
